import Service, { inject as service } from '@ember/service';
import type ExtensionHandlerService from './extension-handler.ts';
import type ExtensionEventsService from './extension-events.ts';
import { ExtensionEventNames } from './extension-events.ts';
import { type ExtensionStructure } from '../../ExtensionHandler.ts';
import { tracked } from '@glimmer/tracking';
import { Extension } from '../../Extension.ts';
import type DomManagerService from './dom-manager.ts';

export default class ExtensionLoaderService extends Service {
  @service('extensions/extension-handler')
  declare extensionHandler: ExtensionHandlerService;
  @service('extensions/extension-events')
  declare extensionEvents: ExtensionEventsService;
  @service('extensions/dom-manager')
  declare domManager: DomManagerService;

  @tracked extensions: Extension[] = [];
  @tracked isReady: boolean = false;

  async loadExtensions(
    extensionDefinitions: ExtensionStructure[],
    owner: object = this
  ): Promise<Extension[]> {
    this.extensionEvents.subscribeToExtensionEvent(
      ExtensionEventNames.EXTENSION_UPDATE,
      (event: Event): void => {
        this.extensionHandler.log('INCOMING EXTENSION EVENT', event);
      }
    );

    const extensions = this.extensionHandler.setupExtensions(
      extensionDefinitions,
      owner
    );

    this.extensions.push(...extensions);
    const loadPromises = this.extensions.map((extension) =>
      extension.load(true, {
        services: {
          domManager: this.domManager,
          extensionHandler: this.extensionHandler
        }
      })
    );

    await Promise.all(loadPromises);

    this.isReady = true;

    return extensions;
  }

  async loadExtension(
    extensionDefinitions: ExtensionStructure,
    owner: object = this
  ): Promise<Extension | undefined> {
    this.extensionEvents.subscribeToExtensionEvent(
      ExtensionEventNames.EXTENSION_UPDATE,
      (event: Event): void => {
        this.extensionHandler.log('INCOMING EXTENSION EVENT', event);
      }
    );

    const extensions = this.extensionHandler.setupExtensions(
      [extensionDefinitions],
      owner
    );

    this.extensions.push(...extensions);

    await extensions[0]?.load(true, {
      services: { domManager: this.domManager }
    });

    this.isReady = true;

    return extensions[0];
  }
}
